/**
 * Flat list of every PLC node that participates in the navigation
 * tree — Controllers, Programs, Routines, AOIs, and PLCDataTypes —
 * with their parent ids so the left rail in {@link PlcTreeNav} can
 * rebuild the hierarchy client-side (same pattern as
 * `getPlantProcessTree`). PLCTags and PLCDataTypeMembers are NOT in
 * the tree; their counts are folded into each owner via `tagCount`
 * (HAS_TAG rollup for containers; HAS_MEMBER count for data types).
 *
 * Hierarchy edges follow the SCADA spec:
 *   Program      -[:BELONGS_TO]-> Controller
 *   Routine      -[:BELONGS_TO]-> Program
 *   AOI          -[:BELONGS_TO]-> Controller
 *   PLCDataType  -[:BELONGS_TO]-> Controller
 *
 * Tag ownership uses the directed HAS_TAG edge from any of the four
 * container kinds to a `PLCTag`. Data-type membership uses
 * `(PLCDataType)-[:HAS_MEMBER]->(PLCDataTypeMember)`.
 *
 * Implementation note: returned as one unified `MATCH (n)` constrained
 * to the five PLC tree labels and discriminated by `labels(n)`,
 * rather than a multi-branch `CALL { … UNION ALL … }`. The latter
 * silently drops the Controller branch in some Neo4j configurations
 * when aggregating directly in `RETURN` without an explicit `WITH` —
 * the unified shape sidesteps that by always materialising the
 * aggregation in a `WITH … count(distinct …)` step.
 *
 * Two pieces of work in one round-trip:
 *
 * 1. The total `PLCTag` node count (label scan, fast) so the headline
 *    subtitle can show the true ontology-wide total rather than the
 *    sum of per-container HAS_TAG edges (which under-reports when
 *    tags have no owner edge or sit outside the four tree kinds).
 *
 * 2. A per-row rail badge count. For container kinds this is a
 *    recursive `HAS_TAG` rollup using `[:BELONGS_TO*0..2]`. For
 *    `PLCDataType` it is the direct `HAS_MEMBER` count.
 */
export const PLANT_PLC_TREE_CYPHER = `
            CALL {
              MATCH (t:PLCTag)
              RETURN count(t) AS totalTagCount
            }
            MATCH (n)
              WHERE (n:Controller OR n:Program OR n:Routine OR n:AOI OR n:PLCDataType)
            OPTIONAL MATCH (n)-[:BELONGS_TO]->(parent)
              WHERE (parent:Controller OR parent:Program)
            CALL {
              WITH n
              OPTIONAL MATCH (n)<-[:BELONGS_TO*0..2]-(desc)-[:HAS_TAG]->(t:PLCTag)
                WHERE (n:Controller OR n:Program OR n:Routine OR n:AOI)
                  AND (desc:Controller OR desc:Program OR desc:Routine OR desc:AOI)
              WITH n, count(distinct t) AS tagRollup
              OPTIONAL MATCH (n)-[:HAS_MEMBER]->(m:PLCDataTypeMember)
                WHERE n:PLCDataType
              WITH n, tagRollup, count(distinct m) AS memberCount
              RETURN CASE
                       WHEN n:PLCDataType THEN memberCount
                       ELSE tagRollup
                     END AS tagCount
            }
            RETURN totalTagCount,
                   n AS node,
                   CASE WHEN parent IS NULL THEN NULL ELSE parent.id END AS parentId,
                   CASE
                     WHEN n:Controller THEN 'Controller'
                     WHEN n:Program THEN 'Program'
                     WHEN n:Routine THEN 'Routine'
                     WHEN n:AOI THEN 'AOI'
                     WHEN n:PLCDataType THEN 'PLCDataType'
                   END AS kind,
                   tagCount,
                   CASE WHEN n:Routine THEN n.type ELSE NULL END AS routineType
            ORDER BY toLower(coalesce(n.name, n.id))
            LIMIT 10000
          `
